import { createContext, ReactElement, ReactNode, useContext } from 'react';

export enum CasinoGamesLoadMoreBtnType {
  BUTTON,
  LAZY_LOAD,
  SLIDER
}

export enum CasinoGamesLayoutType {
  CLASSIC,
  PERSONALIZATION
}

export type CasinoGamesComputedConfigs = {
  loadMoreButtonType: CasinoGamesLoadMoreBtnType;
  loadMoreButtonEnabled: boolean;
  gamesPerRow: number;
  gamesRows: number;
  gamesLimit: number;
  arrowsEnabled: boolean;
  categoryNameVisible: boolean;
  leftSideBarAvailable: boolean;
  gamesCountForSkeleton: number;
  layoutType: CasinoGamesLayoutType;
  nextSlideOnHover?: boolean;
  gameTitle?: boolean;
};

export type CasinoGamesConfigs = {
  isFirstCasinoElement: boolean;
  categoryIds: string;
  customCategoryIds: string;
  providerIds: string;
  moduleId: number;
  casinoGamesLimit: number;
  showCategoryName: boolean;
  showLoadMoreBtn: boolean;
  bindWithCategories: boolean;
  bindWithProviders: boolean;
  gamesPerRow: number;
  groupAllByCategories: boolean;
  aspectRatio: string;
  casinoPlayRealBtnSize: string;
  casinoNameSize: string;
  loadMoreType: number;
  gamesRows: number;
  showArrowForMobile: boolean;
  mobileCustomSettings: boolean;
  mobileShowLoadMoreBtn: boolean;
  mobileLoadMoreType: number;
  mobileCasinoGamesLimit: number;
  mobileGamesRows: number;
  mobileGamesPerRow: number;
  mobileShowArrow: boolean;
  layoutType: CasinoGamesLayoutType;
  skipQueryKeysCheck: boolean;
  favorite?: {
    useAlternativeTitle?: boolean;
    disableProviders?: boolean;
  };
  nextSlideOnHover?: boolean;
  gameTitle: boolean;
  categoriesLength?: number;
};

export type CasinoGamesConfigsProviderProps = {
  children: ReactNode;
  value: {
    configs: Partial<CasinoGamesConfigs>;
    computedConfigs: CasinoGamesComputedConfigs;
  };
};

const defaultConfigs: Partial<CasinoGamesConfigs> = {
  categoryIds: '',
  moduleId: 1,
  casinoGamesLimit: 10,
  showCategoryName: true,
  showLoadMoreBtn: true,
  gamesPerRow: 8,
  casinoPlayRealBtnSize: '44',
  casinoNameSize: '16',
  loadMoreType: 0,
  aspectRatio: '70',
  gamesRows: 1,
  showArrowForMobile: true,
  layoutType: CasinoGamesLayoutType.CLASSIC,
  nextSlideOnHover: false
};

const CasinoGamesConfigsContext = createContext<
  CasinoGamesConfigsProviderProps['value']
>({
  configs: defaultConfigs,
  computedConfigs: {
    gamesRows: 1,
    gamesLimit: 16,
    gamesPerRow: 6,
    gameTitle: false,
    loadMoreButtonEnabled: false,
    loadMoreButtonType: CasinoGamesLoadMoreBtnType.BUTTON,
    arrowsEnabled: false,
    leftSideBarAvailable: false,
    categoryNameVisible: true,
    gamesCountForSkeleton: 6,
    layoutType: CasinoGamesLayoutType.CLASSIC,
    nextSlideOnHover: false
  }
});

export const CasinoGamesConfigsProvider = (
  props: CasinoGamesConfigsProviderProps
): ReactElement => {
  return (
    <CasinoGamesConfigsContext.Provider value={props.value}>
      {props.children}
    </CasinoGamesConfigsContext.Provider>
  );
};

export const useCasinoGamesConfigsValues =
  (): CasinoGamesConfigsProviderProps['value'] => {
    return useContext(CasinoGamesConfigsContext);
  };
